import {
  featureLabel,
  policyLabel,
} from '@app/src/pages/Configurations/ConfigurationsPages/Organization/Access/accessMatrix'
import type { EntitlementFeature, EntitlementPolicy } from '@app/src/types/entitlements'
import { AccessPolicy } from '@app/src/types/entitlements'
import { capitalize } from '@app/src/utils/helpers'
import {
  Autocomplete,
  CircularProgress,
  Divider,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React from 'react'
import OrganizationRules from './OrganizationRules'
import type { PolicyEdits } from './templatePolicy'
import { policyDescription, shownPolicy } from './templatePolicy'

type Props = {
  scopeId: number
  features: EntitlementFeature[]
  policies: EntitlementPolicy[]
  isLoading: boolean
  isError: boolean
  edits: PolicyEdits
  onChange: (feature: EntitlementFeature, policy: AccessPolicy) => void
}

const DefaultAccessPolicy: React.FC<Props> = ({ scopeId, features, policies, isLoading, isError, edits, onChange }) => (
  <Stack spacing={2}>
    <Stack spacing={0.5}>
      <Typography variant="subtitle2">Default access policy</Typography>
      <Typography variant="caption" color="text.secondary">
        What every organization starts with. CS can change it for a single organization on its Access tab.
      </Typography>
    </Stack>

    {isLoading && <CircularProgress size={20} />}

    {isError && (
      <Typography variant="caption" color="error">
        Couldn&apos;t load the access policy for this template.
      </Typography>
    )}

    {!isLoading && !isError && (
      <Stack spacing={2}>
        {features.map(feature => (
          <FeaturePolicy
            key={feature}
            feature={feature}
            scopeId={scopeId}
            policy={shownPolicy(policies, feature, scopeId, edits)}
            onChange={policy => onChange(feature, policy)}
          />
        ))}
      </Stack>
    )}
  </Stack>
)

type FeaturePolicyProps = {
  feature: EntitlementFeature
  scopeId: number
  policy: AccessPolicy
  onChange: (policy: AccessPolicy) => void
}

const FeaturePolicy: React.FC<FeaturePolicyProps> = ({ feature, scopeId, policy, onChange }) => (
  <Paper variant="outlined">
    <Stack spacing={2} p={2} divider={<Divider />}>
      <Stack spacing={1}>
        <Typography variant="subtitle2">{capitalize(featureLabel[feature])}</Typography>
        <Autocomplete
          options={Object.values(AccessPolicy)}
          value={policy}
          onChange={(_event, newPolicy) => onChange(newPolicy)}
          disableClearable
          getOptionLabel={option => policyLabel[option]}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <ListItemText primary={policyLabel[option]} secondary={policyDescription[option]} />
            </li>
          )}
          renderInput={params => (
            <TextField {...params} size="small" inputProps={{ ...params.inputProps, readOnly: true }} />
          )}
        />
        <Typography variant="caption" color="text.secondary">
          {policyDescription[policy]}
        </Typography>
      </Stack>
      <OrganizationRules feature={feature} scopeId={scopeId} />
    </Stack>
  </Paper>
)

export default DefaultAccessPolicy
